/**
 * Which named data-layer source the Incoming tab reads (ClickUp z8nrz7c366).
 *
 * The browser no longer knows data-layer hosts at all. It knows source IDS,
 * from the allowlist cgt-backend serves at `GET /integrations/data-layer/
 * sources`, and every packet request goes through that proxy. The free-text
 * URL + token connection this module used to hold is gone on purpose: once a
 * server fetches on the browser's behalf, a user-supplied host is server-side
 * request forgery, so the field is deleted rather than hidden.
 *
 * Still a module-level store with a subscription (`useSyncExternalStore`
 * shape) rather than React context, because `dataLayerApi` is a plain module
 * and must read the selection per call.
 *
 * Two values, deliberately separate:
 *
 * - the STORED selection — the user's explicit choice, persisted per browser
 * - the server DEFAULT — supplied by `GET sources`, held in memory only
 *
 * The effective source is `stored ?? default`. The default is never persisted:
 * writing it to localStorage would freeze each user on whatever the default
 * was the first time they opened the tab, and "default to the real source" has
 * to stay a backend config change, not a redeploy or a per-user cache bust.
 */

/** Matches the `cgt.auth.*` / `cgt_selected_tenant` convention. */
const STORAGE_KEY = 'cgt.dataLayer.sourceId'

function readStored(): string | null {
  // Guarded because vitest runs in the node environment, where localStorage
  // does not exist. An unguarded read here would throw on import and take out
  // every test that touches the API client.
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

let stored = readStored()
let serverDefault: string | null = null

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/**
 * The source in force, or null while the allowlist has not answered yet.
 * Callers gate their polls on non-null rather than assuming.
 */
export function dataLayerSourceId(): string | null {
  return stored ?? serverDefault
}

/** The user's explicit selection; null to fall back to the server default. */
export function setDataLayerSourceId(id: string | null): void {
  stored = id

  if (typeof localStorage !== 'undefined') {
    try {
      if (id === null) localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, id)
    } catch {
      // Private browsing or a full quota. The selection still applies for this
      // session, which is the part that matters during a demo.
    }
  }

  notify()
}

/**
 * Called by whoever fetched `GET sources`. Also the place a stale stored
 * selection is retired: if the allowlist no longer contains it (a source was
 * renamed or removed server-side), keeping it would poll a 400 forever.
 */
export function setDataLayerServerDefault(defaultId: string, validIds: string[]): void {
  serverDefault = defaultId
  if (stored !== null && !validIds.includes(stored)) {
    setDataLayerSourceId(null)
    return
  }
  notify()
}

export function subscribeDataLayerSourceId(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
